/**
 * CMS API client for the SEO Blog CMS admin (admin/).
 *
 * Reads from the public read-only JSON API (X-Api-Key protected). All
 * `getStaticProps`/`getStaticPaths` callers run server-side, so we use
 * `process.env` (not NEXT_PUBLIC_*). The client never needs the key.
 *
 * Each helper normalizes the admin's flat `data` array into the shape
 * the frontend components already expect (title, slug, thumbImg, date,
 * category[], tag[], shortDescription, fullDescription, author, …) so
 * the existing JSX keeps working without per-page changes.
 *
 * When the API is unreachable or the key is missing, helpers return an
 * empty result set. The site is fully CMS-driven — there are no static
 * JSON fallbacks on the frontend.
 */

// Admin public API base. Local dev default points at the PHP built-in
// server from admin/README.md. Set CMS_API_BASE in .env.local for prod.
const API_BASE =
  process.env.CMS_API_BASE ||
  process.env.NEXT_PUBLIC_CMS_API_BASE ||
  "http://localhost:8000";

// Admin API key. Required for the public read endpoints. Without it
// every call falls back to the bundled static JSON.
const API_KEY =
  process.env.CMS_API_KEY ||
  process.env.NEXT_PUBLIC_CMS_API_KEY ||
  "";

/** Internal fetch with cache + fallback-on-error. */
async function cmsFetch(path, params = {}) {
  if (!API_KEY) return null;
  const url = new URL(API_BASE.replace(/\/$/, "") + path);
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== "") {
      url.searchParams.set(k, String(v));
    }
  });

  // dev mode: skip the Next data cache so edits in the admin show up
  // immediately. In production we rely on `next: { revalidate }` for ISR.
  const fetchOpts =
    process.env.NODE_ENV === "production"
      ? { next: { revalidate: 3600 } }
      : { cache: "no-store" };

  try {
    const res = await fetch(url.toString(), {
      ...fetchOpts,
      headers: { "X-Api-Key": API_KEY, Accept: "application/json" },
    });
    if (!res.ok) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn(`[cms] ${path} → HTTP ${res.status}`);
      }
      return null;
    }
    const json = await res.json();
    if (!json || json.ok !== true) {
      if (process.env.NODE_ENV !== "production") {
        // eslint-disable-next-line no-console
        console.warn(`[cms] ${path} → ok=false`, json);
      }
      return null;
    }
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.log(`[cms] ${path} → ${json.data?.length ?? 0} items (total=${json.meta?.total})`);
    }
    return json;
  } catch (err) {
    if (process.env.NODE_ENV !== "production") {
      // eslint-disable-next-line no-console
      console.warn(`[cms] fetch failed for ${path}: ${err.message}`);
    }
    return null;
  }
}

// ─── normalizers ──────────────────────────────────────────────────
// The admin API returns a flat record per post. We expand it into the
// shape the existing components expect so we don't have to touch JSX.

function formatDate(input) {
  if (!input) return "";
  const d = new Date(input);
  if (Number.isNaN(d.getTime())) return String(input);
  return d.toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
}

/**
 * Normalize a datetime string to ISO 8601 with `T` separator and UTC.
 * CMS / PHP returns `YYYY-MM-DD HH:MM:SS` (space separator, no zone)
 * which fails Google Rich Results validation — needs `T` + `Z`/offset.
 */
function toIsoDate(input) {
  if (!input) return "";
  // Already ISO 8601 with T separator? Pass through.
  if (/^\d{4}-\d{2}-\d{2}T/.test(String(input))) return String(input);
  const s = String(input).trim().replace(" ", "T");
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString();
}

/** Strip basic HTML tags for a one-line excerpt fallback. */
function stripHtml(html) {
  if (!html) return "";
  return String(html)
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/**
 * Inline images authored in the CMS use portable /uploads/... paths. Resolve
 * those paths against the CMS origin before sending the HTML to the browser;
 * the Next.js frontend and PHP admin can live on different origins in dev or
 * production.
 */
function resolveCmsContentUrls(html) {
  if (!html) return "";
  const cmsOrigin = API_BASE.replace(/\/$/, "");
  // FAQ blocks are a separate CMS relation and render as the accordion on the
  // blog page. Strip a legacy body FAQ section if an older post still has one.
  const withoutInlineFaq = String(html).replace(
    /<h2\b[^>]*>\s*(?:Frequently\s+asked\s+questions|FAQs?)\s*<\/h2>[\s\S]*?(?=<h2\b|$)/gi,
    ""
  );
  const resolved = withoutInlineFaq.replace(
    /\b(src|href)=(['"])\/uploads\//gi,
    `$1=$2${cmsOrigin}/uploads/`
  );

  // Raw TinyMCE tables need a scroll container on narrow screens. Keeping
  // the wrapper in the frontend normalizer avoids polluting stored CMS HTML.
  return resolved.replace(
    /<table\b[^>]*>[\s\S]*?<\/table>/gi,
    (table) =>
      `<div class="blog-table-scroll" role="region" aria-label="Scrollable table" tabindex="0">${table}</div>`
  );
}

/** Truncate to a word boundary. */
function excerpt(text, maxLen = 160) {
  const t = (text || "").trim();
  if (t.length <= maxLen) return t;
  return t.slice(0, maxLen).replace(/\s+\S*$/, "") + "…";
}

/**
 * Hard cap at maxLen characters (no word-boundary refinement, no ellipsis).
 * Used for SEO meta fields where the suffix would itself count against the
 * SERP truncation limit and the platform auto-truncates anyway.
 */
function truncateAt(text, maxLen) {
  const t = (text || "").trim();
  if (!t || t.length <= maxLen) return t;
  return t.slice(0, maxLen);
}

/**
 * True when robots_advanced carries any directive that actually changes the
 * emitted meta tag. The default SeoHead output already includes
 * `max-snippet:-1` and `max-image-preview:large`, so the only directives we
 * need to surface from the per-post payload are: noarchive, nosnippet,
 * max_snippet (when set), and max_image_preview (when set to non-default).
 *
 * `noarchive`/`nosnippet` may arrive as boolean false, "0", or 0 — all
 * "not set." A real "1" / true is a directive.
 */
function hasNonDefaultAdvanced(adv) {
  if (!adv || typeof adv !== "object") return false;
  if (adv.noarchive === true || adv.noarchive === 1 || adv.noarchive === "1") return true;
  if (adv.nosnippet === true || adv.nosnippet === 1 || adv.nosnippet === "1") return true;
  // max_snippet: any non-empty value other than -1 (the default) is meaningful.
  if (adv.max_snippet !== undefined && adv.max_snippet !== null && adv.max_snippet !== "") {
    const n = Number(adv.max_snippet);
    if (!Number.isNaN(n) && n !== -1) return true;
    if (Number.isNaN(n)) return true;
  }
  // max_image_preview: only meaningful when set to a non-default value.
  const mip = adv.max_image_preview;
  if (mip && mip !== "large" && mip !== "standard") return true;
  return false;
}

function normalizePost(r) {
  if (!r) return null;
  let seoTitle = r.seo && r.seo.seo_title ? r.seo.seo_title : null;
  const excerptRaw =
    r.excerpt && String(r.excerpt).trim() !== ""
      ? r.excerpt
      : excerpt(stripHtml(r.content || ""), 160);
  // API may return `author` as either the legacy string (just name) or the
  // enriched object from ApiController::authorObject(). Normalize both.
  const a =
    r.author && typeof r.author === "object" && !Array.isArray(r.author)
      ? r.author
      : { name: r.author || "Admin" };
  const authorName = a.name || "Admin";
  // The admin API decodes secondary_keywords_json into r.seo.secondary_keywords
  // (array). Tolerate a JSON string or missing field for legacy responses.
  let secondaryKeywords = [];
  if (r.seo && Array.isArray(r.seo.secondary_keywords)) {
    secondaryKeywords = r.seo.secondary_keywords;
  } else if (r.seo && typeof r.seo.secondary_keywords === "string") {
    try {
      const parsed = JSON.parse(r.seo.secondary_keywords);
      if (Array.isArray(parsed)) secondaryKeywords = parsed;
    } catch (_) {
      secondaryKeywords = [];
    }
  }
  secondaryKeywords = secondaryKeywords
    .map((k) => String(k || "").trim())
    .filter((k) => k !== "");
  const seo = r.seo || {};
  // SERP-friendly safety net: trim CMS-supplied SEO strings to the
  // canonical limits even when the admin saves longer copy. Body content
  // (content, direct_answer) is left untouched — only the fields that
  // surface as <title>, meta description, og:*, twitter:* are capped.
  if (seoTitle) seo.seo_title = truncateAt(seoTitle, 60);
  if (seo.meta_description) seo.meta_description = truncateAt(seo.meta_description, 160);
  if (seo.og_title) seo.og_title = truncateAt(seo.og_title, 60);
  if (seo.og_description) seo.og_description = truncateAt(seo.og_description, 160);
  if (seo.twitter_title) seo.twitter_title = truncateAt(seo.twitter_title, 60);
  if (seo.twitter_description) seo.twitter_description = truncateAt(seo.twitter_description, 160);
  // Mirror the trimmed values back to the local variables so the
  // top-level convenience aliases (seo_title, meta_description) returned
  // to consumers reflect the capped values, not the originals.
  seoTitle = seo.seo_title || null;
  return {
    id: r.id,
    title: r.title,
    slug: r.slug,
    url: r.url,
    thumbImg: r.featured_image || "",
    featured_image: r.featured_image || "",
    date: formatDate(r.published_at || r.updated_at),
    published_at: toIsoDate(r.published_at),
    updated_at: toIsoDate(r.updated_at || r.published_at),
    views: 0,
    category: r.primary_cat ? [r.primary_cat] : r.category_slugs || [],
    category_names: r.category_slugs || [],
    tag: r.tag_slugs || [],
    shortDescription: excerptRaw,
    fullDescription: r.direct_answer || excerptRaw,
    content: resolveCmsContentUrls(r.content || ""),
    faqs: Array.isArray(r.faqs) ? r.faqs : [],
    author: {
      id: a.id ?? null,
      name: authorName,
      slug: a.slug || null,
      username: a.username || null,
      bio: a.bio || "",
      credentials: a.credentials || "",
      role: a.role || "",
      avatar_url: a.avatar_url || null,
      social_links: Array.isArray(a.social_links) ? a.social_links : [],
      same_as: Array.isArray(a.same_as) ? a.same_as : [],
      // Back-compat fields used by older UI consumers.
      img: a.avatar_url || "author.webp",
      description: a.bio || "",
    },
    type: (r.category_slugs && r.category_slugs[0]) || "Blog",
    // Top-level SEO fields for convenience — components that already use
    // blog.seo_title / blog.meta_description keep working unchanged.
    seo: (() => {
      // Drop default robots values from the inline seo object too. The
      // SeoHead on the host page reads its own page-level seo prop, so
      // duplicating the defaults per-post in __NEXT_DATA__ is pure
      // audit-tool noise. Non-default (noindex/nofollow/advanced) values
      // still propagate.
      const { robots_index, robots_follow, robots_advanced, ...rest } = seo;
      const keep = { ...rest };
      if (typeof robots_index === "number" && robots_index === 0) keep.robots_index = 0;
      if (typeof robots_follow === "number" && robots_follow === 0) keep.robots_follow = 0;
      if (hasNonDefaultAdvanced(robots_advanced)) keep.robots_advanced = robots_advanced;
      return keep;
    })(),
    seo_title: seoTitle,
    meta_description: seo.meta_description,
    focus_keyword: seo.focus_keyword,
    secondary_keywords: secondaryKeywords,
    canonical_url: seo.canonical_url || null,
    // Only surface non-default robots values. The defaults (index/follow,
    // no advanced directives) match the meta tag SeoHead already emits on
    // every page, so duplicating them per-post in __NEXT_DATA__ only adds
    // audit-tool noise without changing behavior. Authors who actually flip
    // a page to noindex/nofollow still get the values — just the default
    // path drops them from the hydration payload.
    ...(typeof seo.robots_index === "number" && seo.robots_index === 0
      ? { robots_index: 0 }
      : {}),
    ...(typeof seo.robots_follow === "number" && seo.robots_follow === 0
      ? { robots_follow: 0 }
      : {}),
    ...(hasNonDefaultAdvanced(seo.robots_advanced)
      ? { robots_advanced: seo.robots_advanced }
      : {}),
    og_title: seo.og_title || null,
    og_description: seo.og_description || null,
    og_image: seo.og_image || null,
    twitter_title: seo.twitter_title || null,
    twitter_description: seo.twitter_description || null,
    twitter_image: seo.twitter_image || null,
    twitter_card_type: seo.twitter_card_type || "summary_large_image",
    schema_type: seo.schema_type || null,
    schema_json: seo.schema_json || null,
    speakable_selectors: Array.isArray(seo.speakable_selectors)
      ? seo.speakable_selectors.filter((s) => !!s && s !== "")
      : [],
    breadcrumb_title: seo.breadcrumb_title || null,
    cornerstone: seo.cornerstone ? 1 : 0,
  };
}

function normalizeCategory(r) {
  return {
    id: r.id,
    name: r.name,
    slug: r.slug,
    description: r.description,
    // Preserve count from the API (posts_count / _count.posts / count).
    count:
      r.count ??
      r.posts_count ??
      r.postsCount ??
      (r._count && r._count.posts) ??
      0,
  };
}

function normalizeTag(r) {
  return {
    id: r.id,
    name: r.name,
    slug: r.slug,
    count:
      r.count ??
      r.posts_count ??
      r.postsCount ??
      (r._count && r._count.posts) ??
      0,
  };
}

function normalizePage(r) {
  if (!r) return null;
  let secondaryKeywords = [];
  if (r.seo && Array.isArray(r.seo.secondary_keywords)) {
    secondaryKeywords = r.seo.secondary_keywords;
  } else if (r.seo && typeof r.seo.secondary_keywords === "string") {
    try {
      const parsed = JSON.parse(r.seo.secondary_keywords);
      if (Array.isArray(parsed)) secondaryKeywords = parsed;
    } catch (_) {
      secondaryKeywords = [];
    }
  }
  secondaryKeywords = secondaryKeywords
    .map((k) => String(k || "").trim())
    .filter((k) => k !== "");
  const seo = r.seo || {};
  // Social-share images: use the dedicated OG/Twitter image if the admin
  // uploaded one, otherwise fall back to the page's featured image so
  // shares always have *something* to display. Same fallback for both.
  const featuredImage = r.featured_image || "";
  const ogImage = seo.og_image || featuredImage || null;
  const twImage = seo.twitter_image || featuredImage || seo.og_image || null;
  // API may return `author` as either the legacy string (just name) or the
  // enriched object from ApiController::authorObject(). Normalize both.
  const a =
    r.author && typeof r.author === "object" && !Array.isArray(r.author)
      ? r.author
      : { name: r.author || "" };
  return {
    id: r.id,
    title: r.title,
    slug: r.slug,
    url: r.url,
    excerpt: r.excerpt || "",
    content: r.content || "",
    featured_image: featuredImage,
    // Author display name (joined from users table on the API side).
    // Used for visible "Author: <name>" attribution on the page.
    author: {
      id: a.id ?? null,
      name: a.name || "",
      slug: a.slug || null,
      username: a.username || null,
      bio: a.bio || "",
      credentials: a.credentials || "",
      role: a.role || "",
      avatar_url: a.avatar_url || null,
      social_links: Array.isArray(a.social_links) ? a.social_links : [],
      same_as: Array.isArray(a.same_as) ? a.same_as : [],
    },
    seo: (() => {
      const { robots_index, robots_follow, robots_advanced, ...rest } = seo;
      const keep = { ...rest };
      if (typeof robots_index === "number" && robots_index === 0) keep.robots_index = 0;
      if (typeof robots_follow === "number" && robots_follow === 0) keep.robots_follow = 0;
      if (hasNonDefaultAdvanced(robots_advanced)) keep.robots_advanced = robots_advanced;
      return keep;
    })(),
    seo_title: seo.seo_title,
    meta_description: seo.meta_description,
    focus_keyword: seo.focus_keyword,
    secondary_keywords: secondaryKeywords,
    canonical_url: seo.canonical_url || null,
    // Drop default robots values from the hydration payload — see the
    // matching note in normalizePost(). Non-default (noindex/nofollow/
    // advanced) values still propagate.
    ...(typeof seo.robots_index === "number" && seo.robots_index === 0
      ? { robots_index: 0 }
      : {}),
    ...(typeof seo.robots_follow === "number" && seo.robots_follow === 0
      ? { robots_follow: 0 }
      : {}),
    ...(hasNonDefaultAdvanced(seo.robots_advanced)
      ? { robots_advanced: seo.robots_advanced }
      : {}),
    og_title: seo.og_title || null,
    og_description: seo.og_description || null,
    og_image: ogImage,
    twitter_title: seo.twitter_title || null,
    twitter_description: seo.twitter_description || null,
    twitter_image: twImage,
    twitter_card_type: seo.twitter_card_type || "summary_large_image",
    schema_type: seo.schema_type || null,
    schema_json: seo.schema_json || null,
    speakable_selectors: Array.isArray(seo.speakable_selectors)
      ? seo.speakable_selectors.filter((s) => !!s && s !== "")
      : [],
    breadcrumb_title: seo.breadcrumb_title || null,
    cornerstone: seo.cornerstone ? 1 : 0,
  };
}

// ─── posts ───────────────────────────────────────────────────────

/** Fetch a paginated list of published posts. */
export async function getPosts({
  page = 1,
  perPage = 100,
  q,
  category,
  tag,
  author,
} = {}) {
  const json = await cmsFetch("/api/v1/posts", {
    page,
    per_page: perPage,
    q,
    category,
    tag,
    author,
  });
  if (json && Array.isArray(json.data)) {
    return {
      data: json.data.map(normalizePost).filter(Boolean),
      meta: json.meta || { total: json.data.length, page, per_page: perPage },
    };
  }
  return {
    data: [],
    meta: { total: 0, page, per_page: perPage, last: 1 },
  };
}

/** Fetch a single published post by slug. */
export async function getPostBySlug(slug) {
  const json = await cmsFetch(`/api/v1/posts/${encodeURIComponent(slug)}`);
  if (json && json.data) return normalizePost(json.data);
  return null;
}

/** All published post slugs — for getStaticPaths. */
export async function getAllPostSlugs() {
  const { data } = await getPosts({ perPage: 100 });
  return data
    .map((b) => ({ slug: b.slug }))
    .filter((p) => typeof p.slug === "string" && p.slug.length > 0);
}

// ─── taxonomies ──────────────────────────────────────────────────

export async function getCategories() {
  const json = await cmsFetch("/api/v1/categories");
  if (json && Array.isArray(json.data)) {
    return json.data.map(normalizeCategory);
  }
  return [];
}

export async function getTags() {
  const json = await cmsFetch("/api/v1/tags");
  if (json && Array.isArray(json.data)) {
    return json.data.map(normalizeTag);
  }
  return [];
}

// ─── pages ───────────────────────────────────────────────────────

export async function getPages() {
  const json = await cmsFetch("/api/v1/pages", { per_page: 100 });
  if (json && Array.isArray(json.data)) {
    return json.data.map(normalizePage).filter(Boolean);
  }
  return [];
}

export async function getPageBySlug(slug) {
  const json = await cmsFetch(`/api/v1/pages/${encodeURIComponent(slug)}`);
  if (json && json.data) return normalizePage(json.data);
  return null;
}

export async function getAllPageSlugs() {
  const pages = await getPages();
  return pages.map((p) => ({ slug: p.slug }));
}

// ─── helpers used elsewhere ──────────────────────────────────────

// ─── authors ────────────────────────────────────────────────────

function normalizeAuthor(r) {
  if (!r) return null;
  let social = [];
  if (Array.isArray(r.social_links)) social = r.social_links;
  else if (Array.isArray(r.social)) social = r.social;
  let sameAs = [];
  if (Array.isArray(r.same_as)) sameAs = r.same_as;
  const name = r.name || "";
  const slug =
    r.slug ||
    String(name)
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
  return {
    id: r.id,
    name,
    slug,
    username: r.username || null,
    role: r.role || "author",
    bio: r.bio || "",
    credentials: r.credentials || "",
    avatar: r.avatar_url || r.avatar || "",
    social_links: social,
    same_as: sameAs,
    posts_count: r.posts_count ?? r.count ?? 0,
    created_at: r.created_at || null,
  };
}

export async function getAuthors() {
  const json = await cmsFetch("/api/v1/users");
  if (json && Array.isArray(json.data)) {
    return json.data.map(normalizeAuthor).filter(Boolean);
  }
  return [];
}

export async function getAuthorBySlug(slug) {
  const json = await cmsFetch(`/api/v1/users/${encodeURIComponent(slug)}`);
  if (json && json.data) return normalizeAuthor(json.data);
  // Fallback: derive the author from a published post so the page still
  // renders when the admin's users endpoint is briefly unavailable.
  if (slug) {
    const { data } = await getPosts({ perPage: 100 });
    for (const p of data) {
      const a = p.author;
      if (!a || !a.name) continue;
      const aSlug = encodeURIComponent(String(a.name).toLowerCase());
      if (aSlug === slug) {
        return normalizeAuthor({
          id: null,
          name: a.name,
          slug,
          bio: a.description || "",
          credentials: "",
          social_links: [],
          posts_count: data.filter((x) => x.author && x.author.name === a.name).length,
        });
      }
    }
  }
  return null;
}

export async function getAllAuthorSlugs() {
  const authors = await getAuthors();
  return authors
    .map((a) => ({ slug: a.slug }))
    .filter((p) => typeof p.slug === "string" && p.slug.length > 0);
}

// ─── projects (real-estate listings) ────────────────────────────

/** Map a project row from the admin API into the shape the frontend
 *  components consume. Keeps the original `r` fields too so anything that
 *  already destructured from the legacy products.json keeps working while
 *  callers migrate. */
function normalizeProject(r) {
  if (!r) return null;
  let seoTitle = r.seo && r.seo.seo_title ? r.seo.seo_title : null;
  const excerptRaw =
    r.excerpt && String(r.excerpt).trim() !== ""
      ? r.excerpt
      : excerpt(stripHtml(r.content || ""), 160);
  const seo = r.seo || {};
  // The admin API decodes secondary_keywords_json into r.seo.secondary_keywords
  // (array). Tolerate a JSON string or missing field for legacy responses.
  let secondaryKeywords = [];
  if (r.seo && Array.isArray(r.seo.secondary_keywords)) {
    secondaryKeywords = r.seo.secondary_keywords;
  } else if (r.seo && typeof r.seo.secondary_keywords === "string") {
    try {
      const parsed = JSON.parse(r.seo.secondary_keywords);
      if (Array.isArray(parsed)) secondaryKeywords = parsed;
    } catch (_) {
      secondaryKeywords = [];
    }
  }
  secondaryKeywords = secondaryKeywords
    .map((k) => String(k || "").trim())
    .filter((k) => k !== "");
  const facts = Array.isArray(r.facts) ? r.facts : [];
  const amenities = Array.isArray(r.amenities) ? r.amenities : [];
  const gallery = Array.isArray(r.gallery) ? r.gallery.filter(Boolean) : [];
  const faqs = Array.isArray(r.faqs) ? r.faqs : [];
  const factValue = (label) => {
    const fact = facts.find(
      (item) => String(item?.label || "").trim().toLowerCase() === label.toLowerCase()
    );
    return fact ? String(fact.value || "").trim() : "";
  };
  const normalizeProjectStage = (value) => {
    const stage = String(value || "").trim().toLowerCase().replace(/[\s_-]+/g, "");
    if (stage === "upcoming") return "upcoming";
    if (stage === "completed" || stage === "complete") return "completed";
    if (stage === "ongoing" || stage === "inprogress" || stage === "underconstruction") {
      return "ongoing";
    }
    return "ongoing";
  };
  const projectStage = normalizeProjectStage(
    r.project_stage || factValue("Project Status") || factValue("Status")
  );
  return {
    id: r.id,
    title: r.title,
    heading: seoTitle || r.title,
    slug: r.slug,
    url: r.url,
    excerpt: r.excerpt || "",
    content: r.content || "",
    content2: r.content2 || "",
    direct_answer: r.direct_answer || "",
    description: {
      title: "Description",
      fullDescription: r.direct_answer || excerptRaw,
      shortDescription: excerptRaw,
    },
    location: r.location || "",
    project_stage: projectStage,
    project_status: projectStage.charAt(0).toUpperCase() + projectStage.slice(1),
    configuration:
      factValue("Configurations") || factValue("Configuration"),
    developer_name: r.developer_name || "",
    developer_tagline: r.developer_tagline || "",
    card_blurb: r.card_blurb || "",
    factsAndFeatures: facts.reduce((acc, f) => {
      const k = String(f.label || "").toLowerCase().replace(/\s+/g, "");
      if (k) acc[k] = f.value;
      return acc;
    }, {}),
    facts,
    amenities1: amenities,
    gallery: {
      img1: gallery[0] || "",
      img2: gallery[1] || "",
      img3: gallery[2] || "",
      all: gallery,
    },
    video: r.video_url ? [r.video_url] : [],
    faqs,
    featured: r.featured === 1,
    productImg: r.featured_image || "",
    thumbImg: r.featured_image || "",
    featured_image: r.featured_image || "",
    date: formatDate(r.published_at || r.updated_at),
    published_at: toIsoDate(r.published_at),
    updated_at: toIsoDate(r.updated_at || r.published_at),
    // API may return `author` as either the legacy string (just name) or the
    // enriched object from ApiController::authorObject(). Normalize both.
    author: (() => {
      const a =
        r.author && typeof r.author === "object" && !Array.isArray(r.author)
          ? r.author
          : { name: r.author || "Admin" };
      return {
        id: a.id ?? null,
        name: a.name || "Admin",
        slug: a.slug || null,
        username: a.username || null,
        bio: a.bio || "",
        credentials: a.credentials || "",
        avatar_url: a.avatar_url || null,
        social_links: Array.isArray(a.social_links) ? a.social_links : [],
        same_as: Array.isArray(a.same_as) ? a.same_as : [],
      };
    })(),
    // Strip default robots values from the inline seo object too — the
    // SeoHead component on the project page reads its own page-level seo
    // prop, so duplicating the defaults per-project in __NEXT_DATA__ is
    // pure audit-tool noise.
    seo: (() => {
      const { robots_index, robots_follow, robots_advanced, ...rest } = seo;
      const keep = { ...rest };
      if (typeof robots_index === "number" && robots_index === 0) keep.robots_index = 0;
      if (typeof robots_follow === "number" && robots_follow === 0) keep.robots_follow = 0;
      if (hasNonDefaultAdvanced(robots_advanced)) keep.robots_advanced = robots_advanced;
      return keep;
    })(),
    seo_title: seoTitle,
    meta_description: seo.meta_description,
    focus_keyword: seo.focus_keyword,
    secondary_keywords: secondaryKeywords,
    canonical_url: seo.canonical_url || null,
    og_title: seo.og_title || null,
    og_description: seo.og_description || null,
    og_image: seo.og_image || r.featured_image || null,
    twitter_title: seo.twitter_title || null,
    twitter_description: seo.twitter_description || null,
    twitter_image: seo.twitter_image || seo.og_image || r.featured_image || null,
    twitter_card_type: seo.twitter_card_type || "summary_large_image",
    schema_type: seo.schema_type || null,
    schema_json: seo.schema_json || null,
  };
}

export async function getProjects({
  page = 1,
  perPage = 100,
  q,
  location,
  stage,
} = {}) {
  const json = await cmsFetch("/api/v1/projects", {
    page,
    per_page: perPage,
    q,
    location,
    stage,
  });
  if (json && Array.isArray(json.data)) {
    return {
      data: json.data.map(normalizeProject).filter(Boolean),
      meta: json.meta || { total: json.data.length, page, per_page: perPage },
    };
  }
  return {
    data: [],
    meta: { total: 0, page, per_page: perPage, last: 1 },
  };
}

export async function getProjectBySlug(slug) {
  const json = await cmsFetch(`/api/v1/projects/${encodeURIComponent(slug)}`);
  if (json && json.data) return normalizeProject(json.data);
  return null;
}

export async function getAllProjectSlugs() {
  const { data } = await getProjects({ perPage: 100 });
  return data
    .map((p) => ({ slug: p.slug }))
    .filter((p) => typeof p.slug === "string" && p.slug.length > 0);
}

/** True when the CMS is configured and reachable. */
export function isCmsConfigured() {
  return Boolean(API_KEY);
}
